import React, { useState, useEffect } from 'react';
import { Typography, Button, Stack, Box, Menu, MenuItem, IconButton, Drawer, Collapse } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link, useNavigate } from 'react-router-dom';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import SportsRugbyOutlinedIcon from '@mui/icons-material/SportsRugbyOutlined';
import { useTranslation } from '../../context/TranslationContext';

// Flag Components
const SpanishFlag: React.FC<{ width?: number; height?: number }> = ({ width = 20, height = 15 }) => (
  <svg width={width} height={height} viewBox="0 0 20 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="20" height="15" rx="2" fill="#C60B1E"/>
    <rect y="3.75" width="20" height="7.5" fill="#FFC400"/>
    <rect y="11.25" width="20" height="3.75" fill="#C60B1E"/>
  </svg>
);

const AmericanFlag: React.FC<{ width?: number; height?: number }> = ({ width = 20, height = 15 }) => (
  <svg width={width} height={height} viewBox="0 0 20 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="20" height="15" rx="2" fill="#B22234"/>
    <rect y="1.15" width="20" height="1.15" fill="#FFFFFF"/>
    <rect y="3.46" width="20" height="1.15" fill="#FFFFFF"/>
    <rect y="5.77" width="20" height="1.15" fill="#FFFFFF"/>
    <rect y="8.08" width="20" height="1.15" fill="#FFFFFF"/>
    <rect y="10.38" width="20" height="1.15" fill="#FFFFFF"/>
    <rect y="12.69" width="20" height="1.15" fill="#FFFFFF"/>
    <rect width="8" height="8.08" fill="#3C3B6E"/>
  </svg>
);

// TopBar styled components - now part of the fixed header system
const TopBar = styled('div')<{ isVisible: boolean }>(({ theme, isVisible }) => ({
  backgroundColor: '#455b40',
  padding: '2px 0',
  borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
  width: '100vw',
  // Responsive padding
  [theme.breakpoints.down('xl')]: {
    padding: '3px 0'
  },
  [theme.breakpoints.down('lg')]: {
    padding: '4px 0'
  },
  [theme.breakpoints.down('md')]: {
    display: 'none'
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    padding: '6px 0'
  }
}));

const ContactInfo = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '24px',
  color: '#FFFFFF',
  fontSize: '14px',
  // Responsive gaps and font sizes
  [theme.breakpoints.down('xl')]: {
    gap: '20px',
    fontSize: '13px'
  },
  [theme.breakpoints.down('lg')]: {
    gap: '16px',
    fontSize: '12px'
  },
  [theme.breakpoints.down('md')]: {
    gap: '12px',
    fontSize: '11px'
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    gap: '28px',
    fontSize: '15px'
  }
}));

const ContactItem = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '6px'
}));


// Combined Header Container - contains both TopBar and MainHeader
const HeaderContainer = styled('div')<{ isVisible: boolean }>(({ theme, isVisible }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 999,
  transform: `translateY(${isVisible ? '0' : '-100%'})`,
  transition: 'all 0.3s ease-in-out',
  width: '100vw'
}));

const MainHeader = styled('div')<{ isVisible: boolean }>(({ theme, isVisible }) => ({
  backgroundColor: isVisible ? 'rgba(255, 255, 255, 0.95)' : 'transparent',
  backdropFilter: isVisible ? 'blur(10px)' : 'none',
  padding: '8px 0', // Just a bit bigger than original 0px
  boxShadow: isVisible ? '0 2px 10px rgba(0, 0, 0, 0.1)' : 'none',
  width: '100vw'
}));

const NavButton = styled(Button)<{ isVisible: boolean }>(({ theme, isVisible }) => ({
  color: isVisible ? '#2a3a28' : '#2a3a28',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '1.4rem',
  fontFamily: '"Lora", serif',
  padding: '8px 12px',
  minWidth: 'auto',
  borderRadius: '0',
  whiteSpace: 'nowrap',
  position: 'relative',
  '&:hover': {
    backgroundColor: 'rgba(42, 58, 40, 0.05)',
    color: '#2a3a28',
    borderRadius: '0'
  },
  // Responsive font sizes
  [theme.breakpoints.down('xl')]: {
    fontSize: '1.3rem',
    padding: '7px 10px'
  },
  [theme.breakpoints.down('lg')]: {
    fontSize: '1.2rem',
    padding: '6px 8px'
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '1.1rem',
    padding: '5px 6px'
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    fontSize: '1.6rem',
    padding: '10px 15px'
  },
  // Hide on mobile - will use hamburger menu
  [theme.breakpoints.down('sm')]: {
    display: 'none'
  }
}));

const LetsTalkButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#85b47c',
  color: '#FFFFFF',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '1.2rem',
  fontFamily: '"Lora", serif',
  padding: '8px 24px',
  borderRadius: '12px',
  border: `2px solid #85b47c`,
  position: 'relative',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  '& .MuiButton-startIcon, & .MuiButton-endIcon, & .MuiButton-label': {
    position: 'relative',
    zIndex: 2
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    backgroundColor: '#FFFFFF',
    transition: 'left 0.4s ease',
    zIndex: 1,
    borderRadius: '12px'
  },
  '&::after': {
    content: '"Contacto →"',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    color: '#85b47c',
    fontWeight: 600,
    fontSize: '1.2rem',
    fontFamily: '"Lora", serif',
    zIndex: 3,
    opacity: 0,
    transition: 'opacity 0.4s ease',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  '&:hover': {
    borderColor: '#85b47c',
    backgroundColor: '#85b47c',
    '&::before': {
      left: 0
    },
    '&::after': {
      opacity: 1
    },
    '& span': {
      opacity: 0
    }
  },
  // Responsive sizing
  [theme.breakpoints.down('xl')]: {
    fontSize: '1.1rem',
    padding: '7px 20px'
  },
  [theme.breakpoints.down('lg')]: {
    fontSize: '1rem',
    padding: '6px 18px'
  },
  [theme.breakpoints.down('md')]: {
    display: 'none' // Hide when hamburger menu appears
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    fontSize: '1.3rem',
    padding: '10px 28px'
  }
}));

const DropdownMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    border: '1px solid rgba(0, 0, 0, 0.1)',
    marginTop: '8px',
    minWidth: '280px',
    // Responsive width
    [theme.breakpoints.down('md')]: {
      minWidth: '240px'
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: '200px'
    },
    // Ultra-wide screens
    '@media (min-width: 1920px)': {
      minWidth: '320px'
    }
  },
  '& .MuiList-root': {
    padding: '8px 0'
  }
}));

const DropdownMenuItem = styled(MenuItem)(({ theme }) => ({
  color: '#2a3a28',
  fontSize: '1.2rem',
  fontFamily: '"Lora", serif',
  fontWeight: 500,
  padding: '0',
  minHeight: '48px',
  backgroundColor: 'transparent !important',
  '& a': {
    display: 'block',
    padding: '14px 24px',
    width: '100%',
    textDecoration: 'none',
    color: 'inherit'
  },
  '&:hover': {
    backgroundColor: 'rgba(69, 91, 65, 0.08) !important',
    '& a': {
      color: '#455b41'
    }
  },
  '&.Mui-selected': {
    backgroundColor: 'transparent !important'
  },
  '&.Mui-selected:hover': {
    backgroundColor: 'rgba(69, 91, 65, 0.08) !important'
  },
  '&.Mui-focusVisible': {
    backgroundColor: 'transparent !important'
  },
  // Responsive sizing
  [theme.breakpoints.down('lg')]: {
    fontSize: '1.1rem',
    padding: '12px 20px',
    minHeight: '44px'
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '1rem',
    padding: '10px 18px',
    minHeight: '40px'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem',
    padding: '8px 16px',
    minHeight: '36px'
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    fontSize: '1.3rem',
    padding: '16px 28px',
    minHeight: '52px'
  }
}));

// Mobile Menu Components
const MobileMenuButton = styled(IconButton)(({ theme }) => ({
  display: 'none',
  color: '#2a3a28',
  padding: '12px',
  minWidth: '48px',
  minHeight: '48px',
  '&:hover': {
    backgroundColor: 'rgba(42, 58, 40, 0.05)'
  },
  '&:active': {
    backgroundColor: 'rgba(42, 58, 40, 0.1)'
  },
  [theme.breakpoints.down('md')]: {
    display: 'flex'
  }
}))

const MobileDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: '280px',
    backgroundColor: '#FFFFFF',
    padding: '20px 0',
    // Responsive drawer width
    [theme.breakpoints.down('sm')]: {
      width: '260px',
      padding: '16px 0'
    },
    '@media (max-width: 480px)': {
      width: '240px',
      padding: '12px 0'
    }
  }
}));

const MobileNavItem = styled(Button)(({ theme }) => ({
  color: '#2a3a28',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '1.3rem',
  fontFamily: '"Lora", serif',
  padding: '16px 24px',
  justifyContent: 'space-between',
  width: '100%',
  borderRadius: 0,
  minHeight: '56px',
  touchAction: 'manipulation',
  '&:hover': {
    backgroundColor: 'rgba(42, 58, 40, 0.05)'
  },
  '&:active': {
    backgroundColor: 'rgba(42, 58, 40, 0.1)'
  },
  // Responsive sizing
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.2rem',
    padding: '14px 20px',
    minHeight: '52px'
  },
  '@media (max-width: 480px)': {
    fontSize: '1.1rem',
    padding: '12px 18px',
    minHeight: '48px'
  }
}));

const MobileSubNavItem = styled(Button)(({ theme }) => ({
  color: '#2a3a28',
  textTransform: 'none',
  fontWeight: 400,
  fontSize: '1.1rem',
  fontFamily: '"Lora", serif',
  padding: '12px 40px',
  justifyContent: 'flex-start',
  width: '100%',
  borderRadius: 0,
  minHeight: '48px',
  touchAction: 'manipulation',
  backgroundColor: 'rgba(42, 58, 40, 0.02)',
  '&:hover': {
    backgroundColor: 'rgba(42, 58, 40, 0.08)'
  },
  '&:active': {
    backgroundColor: 'rgba(42, 58, 40, 0.12)'
  },
  // Responsive sizing
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
    padding: '10px 36px',
    minHeight: '44px'
  },
  '@media (max-width: 480px)': {
    fontSize: '0.95rem',
    padding: '8px 32px',
    minHeight: '40px'
  }
}));

const Header: React.FC = () => {
  const { currentLanguage, setLanguage } = useTranslation();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [sobreNosotrosAnchor, setSobreNosotrosAnchor] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSobreNosotrosOpen, setMobileSobreNosotrosOpen] = useState(false);
  const [languageAnchor, setLanguageAnchor] = useState<null | HTMLElement>(null);

  useEffect(() => {
    const controlHeader = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY <= 50) {
        // At the top of the page - show header by default
        setIsVisible(true);
      } else if (currentScrollY < lastScrollY) {
        // Scrolling up - show header
        setIsVisible(true);
      } else {
        // Scrolling down - hide header
        setIsVisible(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', controlHeader);
    
    return () => {
      window.removeEventListener('scroll', controlHeader);
    };
  }, [lastScrollY]);

  const [dropdownTimer, setDropdownTimer] = useState<NodeJS.Timeout | null>(null);

  const handleSobreNosotrosClick = () => {
    navigate('/quienes-somos');
  };

  const handleSobreNosotrosMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    // Clear any existing timer
    if (dropdownTimer) {
      clearTimeout(dropdownTimer);
      setDropdownTimer(null);
    }
    setSobreNosotrosAnchor(event.currentTarget);
  };

  const handleSobreNosotrosMouseLeave = () => {
    // Set a timer to close dropdown
    const timer = setTimeout(() => {
      setSobreNosotrosAnchor(null);
    }, 300);
    setDropdownTimer(timer);
  };

  const handleDropdownMouseEnter = () => {
    // Clear timer when entering dropdown
    if (dropdownTimer) {
      clearTimeout(dropdownTimer);
      setDropdownTimer(null);
    }
  };

  const handleDropdownMouseLeave = () => {
    // Close dropdown when leaving
    setSobreNosotrosAnchor(null);
  };

  const handleClose = () => {
    setSobreNosotrosAnchor(null);
  };


  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
    setMobileSobreNosotrosOpen(false);
  };

  const handleMobileSobreNosotrosToggle = () => {
    setMobileSobreNosotrosOpen(!mobileSobreNosotrosOpen);
  };

  const handleLanguageClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setLanguageAnchor(event.currentTarget);
  };

  const handleLanguageClose = () => {
    setLanguageAnchor(null);
  };

  const handleLanguageSelect = (language: 'ES' | 'EN') => {
    setLanguage(language);
    setLanguageAnchor(null);
  };

  return (
    <>
      {/* Combined Header Container - TopBar and MainHeader move together */}
      <HeaderContainer isVisible={isVisible}>
        {/* Top Contact Bar */}
        <TopBar isVisible={isVisible}>
          <div style={{ maxWidth: '1800px', margin: '0 auto', padding: '2px 20px' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <ContactInfo>
                <ContactItem sx={{ display: { xs: 'none', sm: 'flex' } }}>
                  <SportsRugbyOutlinedIcon sx={{ fontSize: '16px', color: '#c2b8a8' }} />
                  <Typography variant="body2" sx={{ color: '#FFFFFF' }}>Finca ecológica.</Typography>
                </ContactItem>
                <div style={{ width: '1px', height: '20px', backgroundColor: 'rgba(255, 255, 255, 0.3)', margin: '0 8px', display: 'none' }} className="separator" />
                <ContactItem>
                  <LocationOnOutlinedIcon sx={{ fontSize: '16px', color: '#c2b8a8' }} />
                  <Typography variant="body2" sx={{ color: '#FFFFFF', display: { xs: 'none', md: 'block' } }}>Bétera, Valencia</Typography>
                  <Typography variant="body2" sx={{ color: '#FFFFFF', display: { xs: 'block', md: 'none' } }}>SF</Typography>
                </ContactItem>
                <ContactItem sx={{ display: { xs: 'none', lg: 'flex' } }}>
                  <MailOutlineOutlinedIcon sx={{ fontSize: '16px', color: '#c2b8a8' }} />
                  <Typography variant="body2" sx={{ color: '#FFFFFF' }}>info@ekkofood.com</Typography>
                </ContactItem>
                <ContactItem>
                  <PhoneOutlinedIcon sx={{ fontSize: '16px', color: '#c2b8a8' }} />
                  <Typography variant="body2" sx={{ color: '#FFFFFF', display: { xs: 'none', sm: 'block' } }}>+34 684 238 458</Typography>
                  <Typography variant="body2" sx={{ color: '#FFFFFF', display: { xs: 'block', sm: 'none' } }}>Call</Typography>
                </ContactItem>
                
              </ContactInfo>
              
              {/* Language selector temporarily hidden */}
              {/* <Stack direction="row" alignItems="center" gap="16px">
                <div style={{ width: '1px', height: '20px', backgroundColor: 'rgba(255, 255, 255, 0.3)', margin: '0 8px' }} />
                <Button
                  onClick={handleLanguageClick}
                  sx={{
                    color: '#FFFFFF',
                    textTransform: 'none',
                    minWidth: 'auto',
                    padding: '4px 8px',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                  endIcon={<KeyboardArrowDownIcon sx={{ fontSize: '16px', color: '#FFFFFF' }} />}
                >
                  {currentLanguage === 'ES' ? (
                    <>
                      <SpanishFlag width={16} height={12} />
                      <span>ES</span>
                    </>
                  ) : (
                    <>
                      <AmericanFlag width={16} height={12} />
                      <span>EN</span>
                    </>
                  )}
                </Button>
              </Stack> */}
            </Stack>
          </div>
        </TopBar>

        {/* Main Navigation Header */}
        <MainHeader isVisible={isVisible}>
        <div style={{ 
          maxWidth: '1800px', 
          margin: '0 auto', 
          padding: '0 20px', 
          width: '100%',
          // Responsive padding
        }}>
          <Stack direction="row" alignItems="center" width="100%" justifyContent="space-between">
            {/* Brand Name */}
            <Link to="/" style={{ textDecoration: 'none' }}>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontFamily: '"Lato", serif', 
                  fontWeight: 700, 
                  color: isVisible ? '#2a3a28' : '#2a3a28',
                  fontSize: {
                    xs: '1.3rem',
                    sm: '1.5rem',
                    md: '1.6rem',
                    lg: '1.7rem',
                    xl: '1.8rem'
                  },
                  '@media (min-width: 1920px)': {
                    fontSize: '2rem'
                  },
                  minWidth: 'fit-content',
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.8
                  }
                }}
              >
                EKKOFOOD
              </Typography>
            </Link>

            {/* Mobile Menu Button */}
            <MobileMenuButton onClick={handleMobileMenuToggle} sx={{ marginLeft: 'auto' }}>
              <MenuIcon sx={{ fontSize: '2rem' }} />
            </MobileMenuButton>

            {/* Navigation Menu - Desktop */}
            <Stack 
              direction="row" 
              alignItems="center" 
              gap={{
                xs: '4px',
                sm: '6px', 
                md: '8px',
                lg: '10px'
              }}
              sx={{
                position: 'relative',
                flex: 1,
                justifyContent: 'center',
                display: { xs: 'none', md: 'flex' }
              }}
            >
              <Link to="/" style={{ textDecoration: 'none' }}>
                <NavButton isVisible={isVisible}>Inicio</NavButton>
              </Link>
              <NavButton 
                isVisible={isVisible}
                endIcon={<KeyboardArrowDownIcon sx={{ fontSize: '1.7rem' }} />} 
                onClick={handleSobreNosotrosClick}
                onMouseEnter={handleSobreNosotrosMouseEnter}
                onMouseLeave={handleSobreNosotrosMouseLeave}
                aria-controls={sobreNosotrosAnchor ? 'sobre-nosotros-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={sobreNosotrosAnchor ? 'true' : undefined}
              >
                Quiénes Somos
              </NavButton>
              
              {/* Logo Image - Responsive */}
              <Link to="/" style={{ textDecoration: 'none' }}>
                <Box
                  sx={{
                    position: 'absolute',
                    width: { xs: '80px', sm: '100px', md: '120px' },
                    height: { xs: '75px', sm: '95px', md: '110px' },
                    left: '50%',
                    transform: `translateX(-60px) translateY(${isVisible ? '0' : '-100%'})`,
                    '@media (min-width: 768px)': {
                      transform: `translateX(-120px) translateY(${isVisible ? '0' : '-100%'})`
                    },
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 25,
                    pointerEvents: 'auto',
                    top: '-10px',
                    borderRadius: '0px 0px 20px 20px',
                    overflow: 'hidden',
                    opacity: isVisible ? 1 : 0,
                    transition: 'all 0.3s ease-in-out',
                    cursor: 'pointer',
                    '&:hover': {
                      opacity: 0.8
                    }
                  }}
                >
                  <img 
                    src="/EkkoFood (4).png" 
                    alt="Ekko Food Logo" 
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'cover'
                    }}
                  />
                </Box>
              </Link>
              
              <Link to="/cursos" style={{ textDecoration: 'none' }}>
                <NavButton isVisible={isVisible} sx={{ 
                  marginLeft: { 
                    sm: '60px',
                    md: '100px',
                    lg: '140px'
                  }
                }}>Cursos</NavButton>
              </Link>
              <Link to="/noticias-blog" style={{ textDecoration: 'none' }}>
                <NavButton isVisible={isVisible}>Noticias & Blog</NavButton>
              </Link>
              <Link to="/granadas-ecologicas" style={{ textDecoration: 'none' }}>
                <NavButton isVisible={isVisible}>Granadas</NavButton>
              </Link>
            </Stack>
            
            {/* Let's Talk Button - moved to right edge */}
            <LetsTalkButton
              onClick={() => navigate('/contacto')}
              sx={{ minWidth: 'fit-content' }}
            >
              Contacto →
            </LetsTalkButton>
          </Stack>
        </div>
      </MainHeader>
      </HeaderContainer>

      {/* Sobre Nosotros Dropdown */}
      <DropdownMenu
        id="sobre-nosotros-menu"
        anchorEl={sobreNosotrosAnchor}
        open={Boolean(sobreNosotrosAnchor)}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'sobre-nosotros-button',
          onMouseEnter: handleDropdownMouseEnter,
          onMouseLeave: handleDropdownMouseLeave
        }}
        sx={{
          pointerEvents: 'none',
          '& .MuiPaper-root': {
            pointerEvents: 'auto',
            mt: 0.5
          }
        }}
      >
        <DropdownMenuItem onClick={handleClose}>
          <Link to="/mission-vision">
            Misión & Visión
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleClose}>
          <Link to="/la-finca">
            La Finca
          </Link>
        </DropdownMenuItem>
      </DropdownMenu>

      {/* Language Dropdown - temporarily hidden */}
      {/* <DropdownMenu
        id="language-menu"
        anchorEl={languageAnchor}
        open={Boolean(languageAnchor)}
        onClose={handleLanguageClose}
        MenuListProps={{
          'aria-labelledby': 'language-button',
        }}
      >
        <DropdownMenuItem 
          onClick={() => handleLanguageSelect('ES')}
          sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <SpanishFlag width={20} height={15} />
          <span>Español</span>
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => handleLanguageSelect('EN')}
          sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <AmericanFlag width={20} height={15} />
          <span>English</span>
        </DropdownMenuItem>
      </DropdownMenu> */}

      {/* Mobile Navigation Drawer */}
      <MobileDrawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          padding: '16px 20px',
          borderBottom: '1px solid rgba(42, 58, 40, 0.1)'
        }}>
          <Link to="/" style={{ textDecoration: 'none' }} onClick={handleMobileMenuClose}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontFamily: '"Lato", serif', 
                fontWeight: 700, 
                color: '#2a3a28',
                fontSize: '1.2rem',
                cursor: 'pointer'
              }}
            >
              EKKOFOOD
            </Typography>
          </Link>
          <IconButton 
            onClick={handleMobileMenuClose}
            sx={{ 
              color: '#2a3a28',
              padding: '8px'
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        
        <Box sx={{ padding: '8px 0' }}>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <MobileNavItem onClick={handleMobileMenuClose}>
              <span>Inicio</span>
            </MobileNavItem>
          </Link>
          
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Link to="/quienes-somos" style={{ textDecoration: 'none', flex: 1 }}>
              <MobileNavItem 
                onClick={handleMobileMenuClose}
                sx={{ 
                  borderRadius: 0,
                  justifyContent: 'flex-start',
                  '&:hover': {
                    backgroundColor: 'rgba(42, 58, 40, 0.05)'
                  }
                }}
              >
                <span>Quiénes Somos</span>
              </MobileNavItem>
            </Link>
            <IconButton
              onClick={handleMobileSobreNosotrosToggle}
              sx={{
                color: '#2a3a28',
                padding: '12px 16px',
                borderLeft: '1px solid rgba(42, 58, 40, 0.1)',
                borderRadius: 0,
                minWidth: '56px',
                '&:hover': {
                  backgroundColor: 'rgba(42, 58, 40, 0.05)'
                }
              }}
            >
              {mobileSobreNosotrosOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          
          <Collapse in={mobileSobreNosotrosOpen} timeout="auto" unmountOnExit>
            <Link to="/mission-vision" style={{ textDecoration: 'none' }}>
              <MobileSubNavItem onClick={handleMobileMenuClose}>
                Misión & Visión
              </MobileSubNavItem>
            </Link>
            <Link to="/la-finca" style={{ textDecoration: 'none' }}>
              <MobileSubNavItem onClick={handleMobileMenuClose}>
                La Finca
              </MobileSubNavItem>
            </Link>
          </Collapse>
          
          <Link to="/cursos" style={{ textDecoration: 'none' }}>
            <MobileNavItem onClick={handleMobileMenuClose}>
              <span>Cursos</span>
            </MobileNavItem>
          </Link>
          
          <Link to="/noticias-blog" style={{ textDecoration: 'none' }}>
            <MobileNavItem onClick={handleMobileMenuClose}>
              <span>Noticias & Blog</span>
            </MobileNavItem>
          </Link>

          <Link to="/granadas-ecologicas" style={{ textDecoration: 'none' }}>
            <MobileNavItem onClick={handleMobileMenuClose}>
              <span>Granadas</span>
            </MobileNavItem>
          </Link>
        </Box>
        
        <Box sx={{
          padding: '20px',
          borderTop: '1px solid rgba(42, 58, 40, 0.1)',
          marginTop: 'auto'
        }}>
          <Button
            fullWidth
            onClick={() => {
              navigate('/contacto');
              handleMobileMenuClose();
            }}
            sx={{
              backgroundColor: '#85b47c',
              color: '#FFFFFF',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1.1rem',
              fontFamily: '"Lora", serif',
              padding: '12px 24px',
              borderRadius: '12px',
              '&:hover': {
                backgroundColor: '#6a8f61'
              }
            }}
          >
            Contacto →
          </Button>
        </Box>
      </MobileDrawer>
    </>
  );
};

export default Header;